import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, ButtonBase, IconButton, Typography } from '@mui/material';
import type React from 'react';
import { monthGrid } from './memberPreviewCalendar';
import type { MemberPreviewDay } from './memberPreviewTypes';

const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

interface MemberPreviewDatePickerProps {
  days: MemberPreviewDay[];
  months: string[];
  month: string;
  onMonthChange: (month: string) => void;
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
}

const MemberPreviewDatePicker: React.FC<MemberPreviewDatePickerProps> = ({
  days,
  months,
  month,
  onMonthChange,
  selectedDate,
  onSelectDate,
}) => {
  const index = months.indexOf(month);
  const [year, monthNumber] = month.split('-').map(Number);
  const monthName = new Intl.DateTimeFormat('en-US', { month: 'long', timeZone: 'UTC' }).format(
    new Date(Date.UTC(year, monthNumber - 1, 1))
  );
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography sx={{ fontWeight: 600 }}>
          {monthName}{' '}
          <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
            {year}
          </Box>
        </Typography>
        <Box>
          <IconButton aria-label="Previous month" size="small" disabled={index <= 0} onClick={() => onMonthChange(months[index - 1])}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton
            aria-label="Next month"
            size="small"
            disabled={index < 0 || index >= months.length - 1}
            onClick={() => onMonthChange(months[index + 1])}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      <Box role="grid" aria-label={`${monthName} ${year}`} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 40px)', gap: 0.5 }}>
        {WEEKDAYS.map((weekday) => (
          <Typography key={weekday} variant="caption" sx={{ textAlign: 'center', fontWeight: 600 }}>
            {weekday}
          </Typography>
        ))}
        {monthGrid(month, days)
          .flat()
          .map((cell, position) =>
            cell === null ? (
              <Box key={`empty-${String(position)}`} />
            ) : (
              <ButtonBase
                key={cell.date}
                role="gridcell"
                aria-label={cell.date}
                aria-selected={cell.date === selectedDate}
                disabled={!cell.enabled}
                onClick={() => onSelectDate(cell.date)}
                sx={{
                  height: 40,
                  borderRadius: 1,
                  fontWeight: cell.enabled ? 700 : 400,
                  color: cell.date === selectedDate ? 'primary.contrastText' : cell.enabled ? 'text.primary' : 'text.disabled',
                  bgcolor: cell.date === selectedDate ? 'primary.main' : cell.enabled ? 'action.hover' : 'transparent',
                }}
              >
                {cell.day}
              </ButtonBase>
            )
          )}
      </Box>
    </Box>
  );
};

export default MemberPreviewDatePicker;
